import Promise from 'bluebird';
import _ from 'underscore';
import { useContext } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { saveAds, getODataConfig } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { getLogVersion } from '@bingads-webui-campaign-react/asset-library-model';
import { newVideoFromMTObject } from '@bingads-webui-campaign-react/image-experience-video-part';
import { useCopilotLoggers } from '@bingads-webui-campaign-react/copilot-loggers';
import { useWizardService } from '@bingads-webui-react/wizard';

import {
  getLoggerWithoutPageInfo,
  createVideoRepository,
  createImageRepository,
  createFolderRepository,
  uploadVideoMetadata,
} from '@bingads-webui-campaign-react/asset-library-services';

import { useAdsRepository } from '../../common/use-ads-repository';
import { getDefaultCampaign, getDefaultAdGroup } from './constants';

export const useVideoAdsRepository = (defaultCampaign, defaultAdGroup) => {
  const i18n = useContext(I18nContext);
  const { state } = useWizardService();
  const instrumentation = useContext(InstrumentationContext);
  const permissions = useContext(PermissionsContext);
  const { logSuggestionsApplied } = useCopilotLoggers();

  const scenario = instrumentation.createScenario('XandrVideoAdsAdStep');
  const activity = scenario.createActivity('Ad');
  const versionForLog = getLogVersion(permissions);
  const logger = getLoggerWithoutPageInfo(activity, versionForLog, 'XandrVideoAdsAdStep');
  const serverContext = useContext(ServerContext);
  const cid = serverContext.CurrentCustomer.Id;
  const aid = serverContext.CurrentAccount.Id;
  const oDataConfig = getODataConfig(serverContext);
  const { flow, deal } = state.context.wizardData;

  const videoRepository = createVideoRepository({
    oDataConfig,
    aid,
    cid,
  });
  const folderRepository = createFolderRepository({
    i18n,
    logger,
    oDataConfig,
    aid,
    cid,
  });
  const imageRepository = createImageRepository({
    oDataConfig,
    aid,
    cid,
    logger,
    i18n,
    activity,
  });

  const uploadVideoIfNeeded = ({ ads }) => {
    const videoPromises = [];
    ads.data.forEach((ad) => {
      if (ad.data && ad.data.video && !ad.data.video.id) {
        videoPromises.push(uploadVideoMetadata({
          videoMetadata: ad.data.video,
          newVideoFromMTObject,
          folderRepository,
          imageRepository,
          videoRepository,
        }).then((video) => {
          _.extend(ad.data.video, video);
        }));
      }
    });

    if (_.isEmpty(videoPromises)) {
      return Promise.resolve(ads);
    }

    return Promise.all(videoPromises).then(() => ads);
  };

  const saveAdsWithMetadata = (data, context) => uploadVideoIfNeeded(data)
    .then(() => saveAds(data, context))
    .then((adData) => {
      const suggestionIds = _.chain(data.ads.data)
        .map(ad => ad.data.video.recommendationId).compact().value();
      const appliedVideoAssetIds = _.chain(data.ads.data)
        .map(ad => ad.data.video.idString).compact().value();

      logSuggestionsApplied(
        data.campaign.Id,
        data.adGroup.Id,
        undefined /* textSuggestions */,
        undefined /* imageSuggestionIds */,
        suggestionIds,
        appliedVideoAssetIds
      );
      return adData;
    });

  const { load, save } = useAdsRepository({
    saveAds: saveAdsWithMetadata,
    defaultCampaign: defaultCampaign || getDefaultCampaign(i18n, serverContext, flow, deal),
    defaultAdGroup: defaultAdGroup || getDefaultAdGroup(i18n, serverContext, flow, deal),
  });

  return {
    load,
    save,
  };
};
